import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Services() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-doboku.com';
  
  useSEO({
    title: '事業内容 - 土木工事・水道工事・舗装工事 | おかやま土木工事株式会社',
    description: 'おかやま土木工事株式会社の事業内容をご紹介。土木工事、水道工事、舗装工事、外構工事、造成工事、メンテナンスまで幅広く対応。岡山市・倉敷市を中心に確かな技術と丁寧な施工でお客様のニーズにお応えします。',
    keywords: '土木工事,水道工事,舗装工事,外構工事,造成工事,岡山市,倉敷市',
    canonical: '/services',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "土木工事・水道工事・舗装工事",
      "provider": {
        "@type": "LocalBusiness",
        "name": "おかやま土木工事株式会社",
        "url": siteUrl
      },
      "areaServed": {
        "@type": "City",
        "name": "岡山市・倉敷市"
      },
      "description": "土木工事、水道工事、舗装工事、外構工事、造成工事、メンテナンスまで幅広く対応"
    }
  });

  const services = [
    {
      title: '土木工事',
      icon: 'ri-road-map-line',
      description: '道路工事、造成工事、擁壁工事など、あらゆる土木工事に対応しています。',
      image: 'https://public.readdy.ai/ai/img_res/e7d79b215a337ef36f9facbaf0eaa222.jpg',
      details: [
        '道路工事・舗装工事',
        '宅地造成・工業団地造成',
        '擁壁工事・法面工事',
        '河川工事・護岸工事',
        '下水道工事',
        '基礎工事・杭工事'
      ],
      features: [
        '確かな技術力による安全施工',
        '環境に配慮した工法の採用',
        '工期厳守と品質管理の徹底',
        '地域特性を活かした設計提案'
      ]
    },
    {
      title: '水道工事',
      icon: 'ri-drop-line',
      description: '上下水道の新設・修繕・メンテナンスまで、水道工事全般に対応いたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20plumbers%20and%20water%20infrastructure%20workers%20in%20Okayama%2C%20professional%20water%20pipe%20installation%20and%20repair%2C%20modern%20plumbing%20equipment%2C%20water%20main%20construction%2C%20safety%20gear%2C%20clean%20work%20environment%2C%20bright%20daylight%2C%20high%20quality%20plumbing%20work&width=600&height=400&seq=service_detail2&orientation=landscape',
      details: [
        '上水道工事・給水管工事',
        '下水道工事・排水管工事',
        '水道管修繕・漏水修理',
        '給排水設備工事',
        '浄化槽工事',
        '水道メーター交換'
      ],
      features: [
        '24時間緊急対応サービス',
        '最新の漏水探知技術',
        '衛生管理の徹底',
        '長期保証とアフターサービス'
      ]
    },
    {
      title: '舗装工事',
      icon: 'ri-roadster-line',
      description: 'アスファルト舗装、コンクリート舗装など、道路舗装工事全般に対応します。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20road%20paving%20specialists%20in%20Okayama%2C%20asphalt%20and%20concrete%20road%20construction%2C%20professional%20paving%20equipment%20and%20machinery%2C%20smooth%20road%20surface%20construction%2C%20workers%20in%20safety%20gear%2C%20high%20quality%20road%20work%2C%20bright%20sunny%20day&width=600&height=400&seq=service_detail3&orientation=landscape',
      details: [
        'アスファルト舗装',
        'コンクリート舗装',
        '駐車場舗装',
        '歩道舗装',
        '舗装修繕・オーバーレイ',
        'ライン引き・区画線工事'
      ],
      features: [
        '耐久性の高い舗装材料使用',
        '美しい仕上がりと平坦性',
        '交通規制の最小化',
        '環境負荷の軽減'
      ]
    },
    {
      title: '外構工事',
      icon: 'ri-home-4-line',
      description: '住宅や商業施設の外構工事、駐車場整備など、建物周りの環境整備を行います。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20construction%20workers%20building%20exterior%20landscaping%20and%20parking%20areas%20in%20Okayama%2C%20residential%20and%20commercial%20exterior%20construction%2C%20fence%20installation%2C%20driveway%20construction%2C%20professional%20outdoor%20construction%20work%2C%20clean%20organized%20site&width=600&height=400&seq=service_detail4&orientation=landscape',
      details: [
        '住宅外構工事',
        '商業施設外構工事',
        '駐車場整備',
        'フェンス・門扉設置',
        '擁壁・ブロック工事',
        '植栽・造園工事'
      ],
      features: [
        'デザイン性と機能性の両立',
        '建物との調和を重視',
        'メンテナンス性を考慮',
        '予算に応じた提案'
      ]
    },
    {
      title: '造成工事',
      icon: 'ri-landscape-line',
      description: '宅地造成、工業団地造成など、土地の有効活用のための造成工事を行います。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20land%20development%20and%20site%20preparation%20specialists%20in%20Okayama%2C%20residential%20and%20industrial%20land%20grading%20construction%2C%20heavy%20earthmoving%20equipment%2C%20professional%20site%20development%2C%20safety%20protocols%2C%20organized%20construction%20site&width=600&height=400&seq=service_detail5&orientation=landscape',
      details: [
        '宅地造成工事',
        '工業団地造成',
        '農地転用造成',
        '切土・盛土工事',
        '排水設備工事',
        '測量・設計'
      ],
      features: [
        '地盤調査に基づく安全設計',
        '排水計画の最適化',
        '環境保全への配慮',
        '法令遵守の徹底'
      ]
    },
    {
      title: 'メンテナンス',
      icon: 'ri-tools-line',
      description: '完成後のアフターサービスも充実。定期点検や修繕工事まで対応します。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20maintenance%20specialists%20performing%20infrastructure%20inspection%20and%20repair%20in%20Okayama%2C%20professional%20maintenance%20team%20with%20tools%20and%20equipment%2C%20quality%20control%20and%20safety%20inspection%2C%20preventive%20maintenance%20work%2C%20clean%20work%20environment&width=600&height=400&seq=service_detail6&orientation=landscape',
      details: [
        '定期点検・診断',
        '予防保全工事',
        '緊急修繕対応',
        '設備更新工事',
        '清掃・メンテナンス',
        '長期保守契約'
      ],
      features: [
        '迅速な対応体制',
        '予防保全による長寿命化',
        'コストパフォーマンスの最適化',
        '24時間緊急対応'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 bg-cover bg-center" style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20Japanese%20construction%20company%20services%20overview%20in%20Okayama%2C%20comprehensive%20civil%20engineering%20and%20infrastructure%20work%2C%20modern%20construction%20equipment%2C%20professional%20team%2C%20safety-focused%20environment%2C%20bright%20daylight%2C%20high%20quality%20construction%20services&width=1920&height=600&seq=services_hero&orientation=landscape')`
        }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="text-white text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">事業内容</h1>
                <p className="text-xl md:text-2xl">
                  岡山県の土木工事・水道工事・舗装工事のプロフェッショナル
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Detail */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                <strong>サービス詳細</strong>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                おかやま土木工事株式会社では、土木工事から水道工事、舗装工事まで、
                幅広い分野で高品質なサービスを提供しています。
              </p>
            </div>

            <div className="space-y-16">
              {services.map((service, index) => (
                <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                  <div className="lg:w-1/2">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
                    />
                  </div>
                  <div className="lg:w-1/2 space-y-6">
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                        <i className={`${service.icon} text-3xl text-blue-600`}></i>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    <p className="text-lg text-gray-600 leading-relaxed">{service.description}</p>
                    
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-3">対応工事</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {service.details.map((detail, idx) => (
                          <div key={idx} className="flex items-center">
                            <i className="ri-check-line text-blue-600 mr-2"></i>
                            <span className="text-gray-700">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-3">特徴・強み</h4>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center">
                            <i className="ri-star-line text-yellow-500 mr-2"></i>
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              お見積もり・ご相談はお気軽に
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              どんな小さな工事でも、まずはお気軽にご相談ください。
              現地調査・お見積もりは無料で承っております。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                086-012-3456
              </a>
              <a
                href="/#contact"
                className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                お問い合わせフォーム
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}